import React, { createContext, useContext, useEffect, useState } from "react";
import { FilterState } from "../pages/HomePage/interfaces/FilterState";

interface FilterContextProps {
  filterState: FilterState;
  setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

const FilterContext = createContext<FilterContextProps | undefined>(undefined);

export const INITIAL_DEFAULT_FILTER: FilterState = {
  country: [],
  regulator: [],
  classes: [],
  class_name: [],
  date_from: null,
  date_to: null,
  date_issue_from: null,
  date_issue_to: null,
  keywords: [],
  tags: [],
  search: "",
  tech_product: [],
  tech_section_vat_act: [],
  gazette_number: [],
  judges: [],
  status: [],
  city: [],
  subject_matter: [],
  tax_alert: [],
};

const FILTER_URL_KEYS = new Set(Object.keys(INITIAL_DEFAULT_FILTER));

const parseFiltersFromURL = (): FilterState | null => {
  const sp = new URLSearchParams(window.location.search);
  const hasAny = Array.from(sp.keys()).some((k) => FILTER_URL_KEYS.has(k));
  if (!hasAny) return null;

  const next: FilterState = { ...INITIAL_DEFAULT_FILTER };
  sp.forEach((value, key) => {
    if (!FILTER_URL_KEYS.has(key)) return;

    if (
      key === "date_from" ||
      key === "date_to" ||
      key === "date_issue_from" ||
      key === "date_issue_to"
    ) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) (next as any)[key] = d;
      return;
    }

    const current = (next as any)[key];
    if (Array.isArray(current)) {
      if (!current.includes(value)) {
        (next as any)[key] = [...current, value];
      }
    } else {
      (next as any)[key] = value;
    }
  });

  return next;
};

const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialState = parseFiltersFromURL() ?? { ...INITIAL_DEFAULT_FILTER };
  const [filterState, setFilterState] = useState<FilterState>(initialState);

  useEffect(() => {
    const handlePop = () => {
      const parsed = parseFiltersFromURL();
      if (parsed) setFilterState(parsed);
      else setFilterState({ ...INITIAL_DEFAULT_FILTER });
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  return (
    <FilterContext.Provider value={{ filterState, setFilterState }}>
      {children}
    </FilterContext.Provider>
  );
};

const useFilterContext = (): FilterContextProps => {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error("useFilterContext must be used within a FilterProvider");
  return ctx;
};

export { FilterProvider, useFilterContext };
